import React from 'react';
import styled, { css } from 'styled-components';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

interface TooltipContentProps {
    $isOneRow?: boolean;
    $maxWidth?: string;
    $hideBeak?: boolean;
    $whiteSpace?: 'normal' | 'pre' | 'pre-wrap' | 'pre-line';
    $variant?: 'default' | 'info';
    $textAlign?: 'left' | 'center' | 'right' | 'start' | 'end';
    children: React.ReactNode;
    size?: 'small' | 'medium';
    side?: 'top' | 'right' | 'bottom' | 'left';
    align?: 'start' | 'center' | 'end';
    sideOffset?: number;
    collisionPadding?: number;
    background?: string;
}

const defaultBeakStyles = css<TooltipContentProps>`
    &::after {
        content: '';
        position: absolute;
        width: 0;
        height: 0;
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-top: 6px solid ${({ theme }) => theme.root.colors.basicColors['90%']};
    }

    &[data-side='top']::after {
        bottom: -6px;
        left: 50%;
        transform: translateX(-50%) rotate(180deg);
    }

    &[data-side='bottom']::after {
        top: -6px;
        left: 50%;
        transform: translateX(-50%);
    }

    &[data-side='left']::after {
        right: -11px;
        top: 50%;
        transform: translateY(-50%) rotate(-90deg);
    }

    &[data-side='right']::after {
        left: -11px;
        top: 50%;
        transform: translateY(-50%) rotate(90deg);
    }

    /* Корректировка положения стрелки при разных выравниваниях */
    &[data-align='start'][data-side='top']::after,
    &[data-align='start'][data-side='bottom']::after {
        left: 20px;
    }

    &[data-align='end'][data-side='top']::after,
    &[data-align='end'][data-side='bottom']::after {
        left: auto;
        right: 20px;
        transform: translateX(50%) rotate(${({ side }) => (side === 'bottom' ? '0deg' : '180deg')});
    }

    &[data-align='start'][data-side='left']::after,
    &[data-align='start'][data-side='right']::after {
        top: 20px;
    }

    &[data-align='end'][data-side='left']::after,
    &[data-align='end'][data-side='right']::after {
        top: auto;
        bottom: 20px;
    }
`;

const infoBeakStyles = css`
    &::after {
        content: '';
        position: absolute;
        width: 0;
        height: 0;
        border-left: 16px solid transparent;
        border-right: 16px solid transparent;
        border-top: 13px solid ${({ theme }) => theme.root.colors.system.infoDefault};
    }

    &[data-side='top']::after {
        bottom: -13px;
        left: 50%;
        transform: translateX(-50%);
    }

    &[data-side='bottom']::after {
        top: -13px;
        left: 50%;
        transform: translateX(-50%) rotate(180deg);
    }

    &[data-side='left']::after {
        right: -14px;
        top: 50%;
        transform: translateY(-50%) rotate(-90deg);
    }

    &[data-side='right']::after {
        left: -14px;
        top: 50%;
        transform: translateY(-50%) rotate(90deg);
    }
`;

const defaultVariantStyles = css<TooltipContentProps>`
    font-weight: 500;
    font-size: ${props => (props.$isOneRow ? (props.size === 'small' ? '12px' : '14px') : '13px')};
    line-height: ${props => (props.$isOneRow ? (props.size === 'small' ? '16px' : '18px') : '18px')};
    background: ${props => props.background || props.theme.root.colors.basicColors['90%']};
    max-width: ${props => (props.$maxWidth ? props.$maxWidth : '300px')};
    padding: 6px 8px;
    border-radius: 8px;
    color: ${({ theme }) => theme.root.colors.basicColors.inverted['90%']};
    white-space: ${props => (props.$isOneRow ? 'nowrap' : 'normal')};
    text-align: ${({ $textAlign }) => $textAlign};

    ${props => (!props.$isOneRow && !props.$hideBeak ? defaultBeakStyles : '')};
`;

const infoVariantStyles = css<TooltipContentProps>`
    background: ${({ background, theme }) => background || theme.root.colors.system.infoDefault};
    padding: 12px;
    border-radius: 16px;
    max-width: ${({ $maxWidth }) => ($maxWidth ? $maxWidth : '230px')};
    ${infoBeakStyles}
`;

export const StyledTooltipContent = styled(TooltipPrimitive.Content)<TooltipContentProps>`
    overflow: hidden;
    text-overflow: ellipsis;
    z-index: 9999;

    ${props => (props.$variant === 'default' ? defaultVariantStyles : '')};
    ${props => (props.$variant === 'info' ? infoVariantStyles : '')};

    white-space: ${({ $whiteSpace }) => $whiteSpace};
`;

export const TooltipContent = React.forwardRef<HTMLDivElement, TooltipContentProps>(
    ({ children, sideOffset: sideOffsetProp, ...props }, ref) => {
        const sideOffset = sideOffsetProp ?? (props.$variant === 'info' ? 19 : 5);

        return (
            <StyledTooltipContent
                ref={ref}
                {...props}
                sideOffset={sideOffset}
            >
                {children}
            </StyledTooltipContent>
        );
    },
);

TooltipContent.displayName = 'TooltipContent';
